// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL-3.0 section 7(b) apply — see ADDITIONAL-TERMS.md.

// A collapsible right-side event drawer that streams state transitions, so the
// user can see everything happens locally. Closeable via its own × and Escape
// (on a phone the drawer covers its own toggle, so those are the real exits).

type LogKind = 'info' | 'good' | 'warn' | 'bad';

export class EventLog {
  private drawer: HTMLElement;
  private list: HTMLElement;
  private toggleBtn: HTMLElement;
  private open = false;

  constructor(root: HTMLElement) {
    this.drawer = root.querySelector('#event-drawer') as HTMLElement;
    this.list = root.querySelector('#event-list') as HTMLElement;
    this.toggleBtn = root.querySelector('#log-toggle') as HTMLElement;

    this.toggleBtn.addEventListener('click', () => this.toggle());
    (this.drawer.querySelector('#event-close') as HTMLElement)?.addEventListener('click', () =>
      this.setOpen(false),
    );
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.open) this.setOpen(false);
    });
  }

  private timestamp(): string {
    const d = new Date();
    const p = (n: number) => n.toString().padStart(2, '0');
    return `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
  }

  add(message: string, kind: LogKind = 'info'): void {
    const row = document.createElement('div');
    row.className = `event-row event-${kind}`;
    const time = document.createElement('span');
    time.className = 'event-time';
    time.textContent = this.timestamp();
    const text = document.createElement('span');
    text.className = 'event-text';
    text.textContent = message;
    row.append(time, text);
    this.list.appendChild(row);
    this.list.scrollTop = this.list.scrollHeight;

    // Pulse the toggle so activity is noticed even while the drawer is closed.
    if (!this.open) {
      this.toggleBtn.classList.remove('pulse');
      void this.toggleBtn.offsetWidth; // restart the animation
      this.toggleBtn.classList.add('pulse');
    }
  }

  toggle(): void {
    this.setOpen(!this.open);
  }

  setOpen(open: boolean): void {
    this.open = open;
    this.drawer.hidden = !open;
    this.drawer.classList.toggle('open', open);
    this.toggleBtn.setAttribute('aria-expanded', String(open));
    if (open) this.toggleBtn.classList.remove('pulse');
  }

  clear(): void {
    this.list.innerHTML = '';
  }
}
